import React, { useEffect, useState } from "react";
import firebase from "firebase";
import { Form } from "informed";
import { TextInput } from "../Forms";
import * as constants from "../../../constants";
import { IconButton } from "../../ui/Buttons/IconButton";

interface FirebaseAuthProps {
	firebaseApp: firebase.app.App;
	children: React.ReactNode;
}

interface SignInValues {
	email: string;
	password: string;
}

const FirebaseAuth: React.FC<FirebaseAuthProps> = ({ firebaseApp, children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>();

	useEffect(() => {
		const unsubscribe = firebaseApp.auth().onAuthStateChanged(user => {
			setIsAuthenticated(user !== null);
			setIsLoading(false);
		});

		return () => unsubscribe();
	}, [firebaseApp]);

	const signInWithEmailAndPassword = (values: SignInValues) => {
		firebaseApp
			.auth()
			.signInWithEmailAndPassword(values.email, values.password)
			.then(() => {
				setError(undefined);
			})
			.catch(reason => {
				setError(reason.message ?? "Something went wrong while signing you in.");
			});
	};

	const signInWithPopover = () => {
		firebaseApp
			.auth()
			.signInWithPopup(new firebase.auth.GoogleAuthProvider())
			.then(() => {
				setError(undefined);
			})
			.catch(reason => {
				setError(reason.message ?? "Something went wrong while signing you in.");
			});
	};

	if (isLoading) {
		return <div>Loading...</div>;
	}

	if (!isAuthenticated) {
		return (
			<>
				{error && (
					<div className="error m-bottom">{error}</div>
				)}
				<Form<SignInValues> onSubmit={signInWithEmailAndPassword}>
					<TextInput
						field="email"
						id="email"
						placeholder="Email Address"
						isRequired
						validationRegex={constants.EMAIL}
					/>
					<TextInput field="password" id="password" placeholder="Password" type="password" isRequired />
					<button className="btn wide btn-primary">Log In</button>
					<div className="divider"></div>
					<IconButton onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						signInWithPopover();
					}} className="btn-block" variant="primary" icon="google" iconWeight="fab">
						Sign In With Google
					</IconButton>
				</Form>
			</>
		);
	}

	return <>{children}</>;
};

export default FirebaseAuth;
